import React, { useContext, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faUnlockAlt } from "@fortawesome/free-solid-svg-icons";
import { faFacebookF, faLinkedinIn, faInstagram } from "@fortawesome/free-brands-svg-icons";
import { Image, Col, Row, Form, Button, FormCheck, Container, InputGroup } from '@themesberg/react-bootstrap';
import { Link } from "react-router-dom";
import AuthContext from "../context/AuthContext";
import BgImage from "../assets/img/ME_background.jpg";
import brandME from "../assets/img/brand/me.webp";
import { Routes } from "../routes";
import { useLocation, useHistory } from "react-router-dom";
import { Modal } from "@themesberg/react-bootstrap";
import { QRCodeSVG } from "qrcode.react";
import { passwordRules } from "../config/passwordPolicy";

function useQueryFromHash() {
  const location = useLocation();
  const search = location.search || ""; // e.g. "#/login?reason=session_expired"
  // Extract query string from the hash
  const queryString = search.includes("?") ? search.split("?")[1] : "";

  return new URLSearchParams(queryString);
}

const LoginPage = () => {
  const {
    loginUser,
    mfaRequired,
    mfaCode,
    setMfaCode,
    verifyMfaCode,
    newPasswordRequired,
    completeNewPassword,
    mfaSetupRequired,
    totpSecretCode,
    confirmMfaEnrollment,
  } = useContext(AuthContext);

  const [totpCode, setTotpCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordChecks, setPasswordChecks] = useState({
    hasLower: false,
    hasUpper: false,
    hasNumber: false,
    hasSpecial: false,
    hasMinLength: false,
    noSpaces: true,
    passwordsMatch: true,
  });

  useEffect(() => {
    const noLeadingOrTrailingSpaces = newPassword === newPassword.trim();

    setPasswordChecks({
      hasLower: passwordRules.lowerCaseRegex.test(newPassword),
      hasUpper: passwordRules.upperCaseRegex.test(newPassword),
      hasNumber: passwordRules.numberRegex.test(newPassword),
      hasSpecial: passwordRules.specialCharsRegex.test(newPassword),
      hasMinLength: newPassword.length >= passwordRules.minLength,
      noSpaces: noLeadingOrTrailingSpaces,
      passwordsMatch: newPassword === confirmPassword,
    });
  }, [newPassword, confirmPassword]);

  useEffect(() => {
    setMfaCode(""); // reset MFA code when component loads
  }, []);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const history = useHistory();

  const query = useQueryFromHash();
  const reason = query.get("reason");
  useEffect(() => {
    if (reason === "session_expired") {
      setModalMessage("Your session has expired. Please log in again.");
      setShowModal(true);
      setTimeout(() => {
        setShowModal(false);
        history.replace("/login");
      }, 3000); // auto-close after 3s
    } else if (reason === "auth_error") {
      setModalMessage("Authentication failed. Please log in.");
      setShowModal(true);
      const newUrl = window.location.pathname;
      history.replace(newUrl);
    }
  }, [reason]);
  const handleSubmit = (e) => {
    e.preventDefault();
    window.localStorage.clear();
    if (username.length > 0 && password.length > 0) {
      loginUser(username, password); // Call the loginUser function
    }
  };

  const handleMfaSubmit = (e) => {
    e.preventDefault();
    verifyMfaCode(); // Call the function to verify MFA code
  };

  const isPasswordValid = Object.values(passwordChecks).every(Boolean);

  return (
    <main
      style={{
        backgroundImage: `url(${BgImage})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '100vh'
      }}
    >
      <section className="d-flex align-items-center my-5 mt-lg-6 mb-lg-5">
        <Container>
          <Row className="justify-content-center form-bg-image mb-2">
            <Col xs={12} className="d-flex align-items-center justify-content-center">
              <Image src={brandME} alt="ME logo" className="image-sm" />
            </Col>
          </Row>
          <Row className="justify-content-center form-bg-image">
            <Col xs={12} className="d-flex align-items-center justify-content-center">
              <div className="bg-white shadow-soft border rounded border-light p-4 p-lg-5 w-100 fmxw-500">
                <div className="text-center text-md-center mb-4 mt-md-0">
                  <p className="text-left fs-3 fw-bold">Login</p>
                </div>
                {mfaSetupRequired ? (
                  <Form className="mt-4" onSubmit={(e) => {
                    e.preventDefault();
                    confirmMfaEnrollment(totpCode);
                  }}>
                    <div className="text-center mb-3">
                      <p>Scan this QR code in your authenticator app:</p>
                      {/* Rendered client-side so the TOTP secret never leaves the browser. */}
                      <QRCodeSVG value={totpSecretCode} size={200} includeMargin aria-label="MFA QR Code" />
                    </div>
                    <Form.Group className="mb-3">
                      <Form.Label>Enter MFA Code from Authenticator</Form.Label>
                      <Form.Control
                        type="text"
                        required
                        value={totpCode}
                        onChange={(e) => setTotpCode(e.target.value)}
                      />
                    </Form.Group>
                    <Button type="submit" className="w-100">Confirm MFA Setup</Button>
                  </Form>
                ) :newPasswordRequired ? (
                  // New Password Required Form
                  <Form className="mt-4" onSubmit={(e) => {
                    e.preventDefault();
                    if (!isPasswordValid) {
                      setModalMessage("Password does not meet the required criteria.");
                      setShowModal(true);
                      return;
                    }
                    completeNewPassword(newPassword);
                  }}>
                    <Form.Group className="mb-4">
                      <Form.Label>Set a New Password</Form.Label>
                      <InputGroup>
                        <Form.Control
                          id="newPassword"
                          type="password"
                          required
                          placeholder="Enter new password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                        />
                      </InputGroup>
                    </Form.Group>
                    <Form.Group className="mb-4">
                      <Form.Label>Confirm Password</Form.Label>
                      <InputGroup>
                        <Form.Control
                          id="confirmPassword"
                          required
                          type="password"
                          placeholder="Re-enter your new password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                      </InputGroup>
                    </Form.Group>

                    {/* Password validation checklist */}
                    <ul style={{ fontSize: "0.9rem", paddingLeft: "1rem" }}>
                      <li style={{ color: passwordChecks.hasLower ? "green" : "red" }}>
                        {passwordChecks.hasLower ? "✓" : "✗"} Password must contain a lower case letter
                      </li>
                      <li style={{ color: passwordChecks.hasUpper ? "green" : "red" }}>
                        {passwordChecks.hasUpper ? "✓" : "✗"} Password must contain an upper case letter
                      </li>
                      <li style={{ color: passwordChecks.hasNumber ? "green" : "red" }}>
                        {passwordChecks.hasNumber ? "✓" : "✗"} Password must contain a number
                      </li>
                      <li style={{ color: passwordChecks.hasMinLength ? "green" : "red" }}>
                        {passwordChecks.hasMinLength ? "✓" : "✗"} Password must be at least 16 characters
                      </li>
                      <li style={{ color: passwordChecks.hasSpecial ? "green" : "red" }}>
                        {passwordChecks.hasSpecial ? "✓" : "✗"} Password must contain a special character or a space
                      </li>
                      <li style={{ color: passwordChecks.noSpaces ? "green" : "red" }}>
                        {passwordChecks.noSpaces ? "✓" : "✗"} No leading or trailing spaces
                      </li>
                      <li style={{ color: passwordChecks.passwordsMatch ? "green" : "red" }}>
                        {passwordChecks.passwordsMatch ? "✓" : "✗"} Passwords must match
                      </li>
                    </ul>

                    <Button variant="primary" type="submit" className="w-100" disabled={!isPasswordValid}>
                      Set New Password
                    </Button>
                  </Form>
                ) : mfaRequired ? (
                  // MFA Code input form
                  <Form className="mt-4" onSubmit={handleMfaSubmit}>
                    <Form.Group className="mb-4">
                      <Form.Label>Enter MFA Code</Form.Label>
                      <InputGroup>
                        <Form.Control
                          id="mfaCode"
                          autoFocus
                          required
                          type="text"
                          placeholder="Enter the MFA code"
                          value={mfaCode}
                          onChange={(e) => setMfaCode(e.target.value)}
                        />
                      </InputGroup>
                    </Form.Group>
                    <Button variant="primary" type="submit" className="w-100">
                      Verify MFA Code
                    </Button>
                  </Form>
                ) : (
                  // Regular login form
                  <Form className="mt-4" onSubmit={handleSubmit}>
                    <Form.Group className="mb-4">
                      <Form.Label>Your Username</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>
                          <FontAwesomeIcon icon={faEnvelope} />
                        </InputGroup.Text>
                        <Form.Control
                          id="username"
                          autoFocus
                          required
                          type="text"
                          placeholder="e.g guest@munichelectrification.com"
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                        />
                      </InputGroup>
                    </Form.Group>
                    <Form.Group>
                      <Form.Group className="mb-4">
                        <Form.Label>Your Password</Form.Label>
                        <InputGroup>
                          <InputGroup.Text>
                            <FontAwesomeIcon icon={faUnlockAlt} />
                          </InputGroup.Text>
                          <Form.Control
                            id="password"
                            required
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                          />
                        </InputGroup>
                      </Form.Group>
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <Form.Check type="checkbox">
                          <FormCheck.Input id="defaultCheck5" className="me-2" />
                          <FormCheck.Label htmlFor="defaultCheck5" className="mb-0">Remember me</FormCheck.Label>
                        </Form.Check>
                      </div>
                    </Form.Group>
                    <Button variant="primary" type="submit" className="w-100">
                      Sign in
                    </Button>
                  </Form>
                )}

                {/* Forgot Password Link */}
                <div className="text-center mt-3">
                  <Link to={Routes.ForgotPassword.path} className="text-decoration-none">
                    Forgot Password?
                  </Link>
                </div>

                <div className="mt-3 mb-4 text-center">
                  <span className="fw-normal"> Find us on</span>
                </div>
                <div className="d-flex justify-content-center my-4">
                  <Button
                    variant="outline-light"
                    className="btn-icon-only btn-pill text-facebook me-2"
                    href="https://www.linkedin.com/company/munichelectrification"
                  >
                    <FontAwesomeIcon icon={faLinkedinIn} />
                  </Button>
                  <Button
                    variant="outline-light"
                    className="btn-icon-only btn-pill text-twitter me-2"
                    href="https://www.instagram.com/munichelectrification/"
                  >
                    <FontAwesomeIcon icon={faInstagram} />
                  </Button>
                  <Button
                    variant="outline-light"
                    className="btn-icon-only btn-pil text-dark"
                    href="https://www.facebook.com/munichelectrification"
                  >
                    <FontAwesomeIcon icon={faFacebookF} />
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Notification</Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalMessage}</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowModal(false)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

    </main>
  );
};

export default LoginPage;